/**
 * ダーク/ライトテーマ切替。
 * - 選好値は admins/{uid}.uiTheme に保存
 * - system モードの場合は OS 設定を追従
 * 仕様書: docs/01_spec/spec_design_system.md 12
 */
import { doc } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { db } from '@/lib/firebase';
import { safeUpdateDoc } from '@/lib/safeWrite';

import { useAuth } from './useAuth';

export type Theme = 'dark' | 'light';
export type ThemePreference = Theme | 'system';

interface ThemeContextValue {
  theme: Theme;
  preference: ThemePreference;
  toggleTheme: () => void;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [preference, setPreferenceState] = useState<ThemePreference>(
    user?.uiTheme ?? 'dark',
  );
  const [theme, setTheme] = useState<Theme>(
    preference === 'system' ? resolveSystemTheme() : preference,
  );

  // user 選好値の変化を反映
  useEffect(() => {
    if (!user?.uiTheme) return;
    setPreferenceState(user.uiTheme);
  }, [user?.uiTheme]);

  // preference → theme
  useEffect(() => {
    if (preference === 'system') {
      setTheme(resolveSystemTheme());
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    setTheme(preference);
    return undefined;
  }, [preference]);

  // DOM に data-theme 属性を適用
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setPreference = useCallback(
    async (p: ThemePreference) => {
      setPreferenceState(p);
      if (user?.uid) {
        try {
          await safeUpdateDoc(doc(db, 'admins', user.uid), { uiTheme: p });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[useTheme] failed to persist uiTheme:', err);
        }
      }
    },
    [user?.uid],
  );

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    void setPreference(next);
  }, [theme, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, preference, toggleTheme, setPreference }),
    [theme, preference, toggleTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
